import { staticPath } from "faqSrc/lib/$path";
import useTranslation from "next-translate/useTranslation";
import React, { VFC } from "react";
import styled from "styled-components";
import Image from "~/components/atoms/Image";
import { generateAmpHelpfulButtonScriptString } from "~/components/modules/qaActions/AmpHelpfulButton";
import {
  HeaderTocProps,
  QaHeaderTocArrowIcon,
  QaHeaderTocBackdrop,
  QaHeaderTocButton,
  QaHeaderTocCard,
} from "~/components/modules/qaFixedHeader/HeaderToc";
import TocActions from "~/components/modules/qaFixedHeader/TocActions";
import AmpTocContent from "~/components/modules/toc/AmpTocContent";

const scriptId = "qa-header-toc-script";
const tocButtonId = "qa-header-toc-button";
const tocButtonIconId = "qa-header-toc-icon";
const tocId = "qa-header-toc";
const tocCardId = "qa-header-toc-card";
const tocBackdropId = "qa-header-toc-backdrop";

const AmpHeaderToc: VFC<HeaderTocProps> = ({
  increaseFeedbackHelpfulUrl,
  lineShareUrl,
  questionAnswer,
  twitterShareUrl,
}) => {
  const { t } = useTranslation();
  return (
    <amp-script script={scriptId} layout="container">
      <QaHeaderTocButton id={tocButtonId}>
        {t("common:right-sidebar-heading")}
        <QaHeaderTocArrowIcon id={tocButtonIconId}>
          <Image
            width="12"
            height="12"
            alt="icon arrow"
            src={staticPath.img.icon_arrow_down_gray_svg}
            layout="fixed"
            ampLayout="fixed"
          />
        </QaHeaderTocArrowIcon>
      </QaHeaderTocButton>
      <TocContainer id={tocId}>
        <QaHeaderTocCard id={tocCardId}>
          <TocActions
            isAmp
            lineShareUrl={lineShareUrl}
            questionAnswerId={questionAnswer.id}
            topicId={questionAnswer.topicId}
            twitterShareUrl={twitterShareUrl}
          />
          <AmpTocContent
            answerTableOfContentHtml={questionAnswer.answerTableOfContentHtml}
          />
        </QaHeaderTocCard>
        <QaHeaderTocBackdrop id={tocBackdropId} />
      </TocContainer>
      <div
        dangerouslySetInnerHTML={{
          __html: `
        <script id="${scriptId}" type="text/plain" target="amp-script">
          (() => {
            ${generateAmpHelpfulButtonScriptString({
              increaseFeedbackHelpfulUrl,
              questionAnswerId: questionAnswer.id,
            })}

            const tocButton = document.getElementById("${tocButtonId}");
            const tocIcon = document.getElementById("${tocButtonIconId}");
            const tocContainer = document.getElementById("${tocId}");
            const tocBackdrop = document.getElementById("${tocBackdropId}");

            if(!tocButton || !tocIcon|| !tocContainer || !tocBackdrop) {
              return;
            }

            tocButton.addEventListener("click", onTocButtonClick);
            tocBackdrop.addEventListener("click", onBackdropClick);
            tocContainer.addEventListener("click", onTocContainerClick);

            function onTocButtonClick() {
              toggleToc();

              // calling methods from generateAmpHelpfulButtonScriptString
              updateHelpfulButton(getHasIncreasedHelpfulFromLocalStorage());
            }

            function onBackdropClick(e) {
              hideToc();
            }

            function onTocContainerClick(e) {
              if(!e.target){
                return;
              }
              hideToc();
            }

            function toggleToc() {
              if(tocContainer.classList.contains("open")) {
                hideToc();
              } else {
                showToc();
              }
            }

            function hideToc() {
              tocContainer.classList.remove("open");
              tocContainer.style.display = "none";
              tocIcon.classList.remove("open");
            }

            function showToc() {
              tocContainer.classList.add("open");
              tocContainer.style.display = "block";
              tocIcon.classList.add("open");
            }
          })();
        </script>
      `,
        }}
      />
    </amp-script>
  );
};

const TocContainer = styled.div`
  visibility: hidden;

  &.open {
    visibility: visible;
  }
`;

export default AmpHeaderToc;
